"use client";

import Link from "next/link";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, Landmark, Activity, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

export function VirtualAccountDetailsModal({ 
  open, 
  onOpenChange, 
  account 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  account: any;
}) {
  if (!account) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Virtual Account Details</DialogTitle>
          <DialogDescription>
            Comprehensive view of account information and activity.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Top Header Card */}
          <div className="p-4 rounded-xl border bg-muted/10 flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">Account Name</p>
              <p className="font-bold text-lg">{account.name}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-mono text-sm">{account.acc}</span>
                <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-primary">
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <Badge className={cn(
              "text-[9px] h-5 px-2 font-bold uppercase border-none",
              account.type === "success" ? "bg-emerald-100 text-emerald-700" :
              account.type === "warning" ? "bg-amber-100 text-amber-700" :
              "bg-muted text-muted-foreground"
            )}>
              {account.status}
            </Badge>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1 p-3 rounded-lg bg-muted/5 border border-muted/20">
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                <Landmark className="h-3.5 w-3.5" />
                <p className="text-[10px] font-bold uppercase tracking-wider">Provider</p>
              </div>
              <p className="text-sm font-semibold">{account.bank}</p>
            </div>

            <div className="space-y-1 p-3 rounded-lg bg-muted/5 border border-muted/20">
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                <Activity className="h-3.5 w-3.5" />
                <p className="text-[10px] font-bold uppercase tracking-wider">Type</p>
              </div>
              <Badge variant="outline" className={cn(
                "text-[9px] h-4 px-1.5 font-bold uppercase border",
                account.accType === "Dedicated" ? "border-primary/30 text-primary bg-primary/5" : "border-amber-500/30 text-amber-600 bg-amber-50"
              )}>
                {account.accType}
              </Badge>
            </div>

            <div className="space-y-1 p-3 rounded-lg bg-muted/5 border border-muted/20">
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                <span className="text-xs font-bold text-muted-foreground">₦</span>
                <p className="text-[10px] font-bold uppercase tracking-wider">Total Inflow</p>
              </div>
              <p className="text-sm font-semibold">{account.amount}</p>
            </div>

            <div className="space-y-1 p-3 rounded-lg bg-muted/5 border border-muted/20">
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                <Calendar className="h-3.5 w-3.5" />
                <p className="text-[10px] font-bold uppercase tracking-wider">Created</p>
              </div>
              <p className="text-sm font-semibold">Oct 24, 2023</p>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Quick Actions</h4>
            <div className="grid grid-cols-2 gap-2">
              <Link href={`/dashboard/virtual-accounts/${account.acc}`}>
                <Button variant="outline" className="w-full text-xs font-semibold h-9" onClick={() => onOpenChange(false)}>
                  View Transactions
                </Button>
              </Link>
              <Button variant="outline" className="text-xs font-semibold h-9 text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/20" onClick={() => {}}>Deactivate</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
